import { getContext, put, call, select, takeEvery } from 'redux-saga/effects';
import { Action } from 'redux';
import { SubmissionError, FormAction } from 'redux-form';
import { ApolloError } from 'apollo-client';
import { Services } from 'init/services';
import { SagaContext } from '../../../../types/common';
import { languageSelector } from '../../../../selectors/app';
import { routine, codeRoutine, ACTIVATE, DEACTIVATE } from './actions';
import { actions } from '../../actions';
import { sessionId } from '../../selectors';
import { SendingMethod } from '../../types';
import { FormData } from './constraints';
import { Props } from './index';
import * as verificationMutation from './verificationMutation.gql';
import * as sendCodeMutation from './sendCodeMutation.gql';
import { VerificationStepVariables, VerificationStep } from './__generated__/VerificationStep';
import { SendCodeVariables } from './__generated__/SendCode';
import { getFormErrors } from '../../../../utils/formatError';
import { featureFlagsSelector } from 'selectors/config';
import { tracked } from 'shared/utils/sagaWrappers';

interface VerificationFormAction extends FormAction {
  payload: {
    values: FormData;
    props: Props;
  };
}

interface CodeFormAction extends FormAction {
  payload: SendingMethod;
}

// @translate
type Errors =
  | 'Wrong verification code'
  | 'Registration with given sessionId has exceeded maximum verification attempts count';

function* sendCode({ payload: method }: CodeFormAction) {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  const variables: SendCodeVariables = {
    sessionId: yield select(sessionId),
    locale: yield select(languageSelector),
    method,
  };

  try {
    yield put(actions.setCodeSource(method));
    yield call(apolloClient.mutate, { mutation: sendCodeMutation, variables });
    yield put(codeRoutine.success());
  } catch (e) {
    yield put(codeRoutine.failure());
  }
}

const makeSaga = (next: Action) =>
  function* saga() {
    const { sendRegistrationCodeToEmail }: ReturnType<typeof featureFlagsSelector> = yield select(
      featureFlagsSelector
    );
    if (sendRegistrationCodeToEmail) {
      yield sendCode({ type: codeRoutine.TRIGGER, payload: SendingMethod.EMAIL });
    }
    const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
      SagaContext.ApolloClient
    );

    yield takeEvery(codeRoutine.TRIGGER, sendCode);

    yield takeEvery(
      routine.TRIGGER,
      function* ({
        payload: {
          values: { verificationCode },
        },
      }: VerificationFormAction) {
        const variables: VerificationStepVariables = {
          sessionId: yield select(sessionId),
          verificationCode: verificationCode.replace(/\s+/g, ''),
        };

        try {
          const { data }: { data: VerificationStep } = yield call(apolloClient.mutate, {
            mutation: verificationMutation,
            variables,
          });
          yield put(routine.success(data));
          yield put(
            actions.setSuggestionLogin(data.userDirectory.fullRegistrationStepTwo.suggestedLogin)
          );
          yield put(next);
        } catch (e: ApolloError | any) {
          const errors = getFormErrors(e, () => '_error');
          yield put(routine.failure(new SubmissionError<FormData, Errors>(errors)));
        }
      }
    );
  };
export default (next: Action) => tracked(makeSaga(next), ACTIVATE, DEACTIVATE);
